<script setup lang="ts">
import { userModule } from '@/store/user/module'
import { computed } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
import { assignmentModule } from '@/store/assignment/module'
const {
    Icon,
} = UIKit

const { assignment, isSelected } = defineProps<{
    assignment: Study.Class.PrebuiltQuizJSON
    isSelected: boolean
}>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const formattedDueDate = computed(() => {
    const assignmentDueDateIsoDate = assignment.dueDate?.iso

    if (assignmentDueDateIsoDate) {
        return assignmentModule.getters.getAssignmentFormattedDate(assignmentDueDateIsoDate)
    }

    return null
})

const hoursUntilAssignmentDue = computed(() => {
    return assignmentModule.getters.getHoursUntilAssignmentIsDue(assignment)
})

const isDueWithinTwentyFourHours = computed(() => {
    return hoursUntilAssignmentDue.value && hoursUntilAssignmentDue.value < 24
})

const isDueWithinTwentyFourHoursTimeString = computed(() => {
    if (isDueWithinTwentyFourHours.value && hoursUntilAssignmentDue.value) {
        const hoursUntilDue = hoursUntilAssignmentDue.value
        if (hoursUntilDue >= 1) {
            const hours = Math.floor(hoursUntilDue)
            return `in ${hours} ${hours > 1 ? 'hours' : 'hour'}`
        }

        const minutes = Math.floor(hoursUntilDue * 60)
        // example: if due in 20 seconds we still want to display due in 1 min
        return `in ${minutes > 1 ? minutes : 1} ${minutes > 1 ? 'mins' : 'min'}`
    }

    return null
})
</script>

<template>
    <div
        v-dark
        class="assignment-card"
        :class="{ 
            'assignment-card--selected': isSelected
        }"
        :tabindex="0"
        @mousedown.prevent
    >
        <div v-dark class="assignment-card__title-info">
            <div v-dark class="assignment-card__name">
                {{ assignment.name }}
            </div>
            <div
                v-if="formattedDueDate"
                v-dark 
                class="assignment-card__due-date"
                :class="{ 
                    'assignment-card__due-date--today': isDueWithinTwentyFourHours
                }"
            >
                <Icon
                    v-if="isDueWithinTwentyFourHours"
                    v-dark
                    class="assignment-card__warning-icon"
                    type="warning"
                    :is-dark-mode="isDarkMode"
                />
                Due {{ isDueWithinTwentyFourHours ? isDueWithinTwentyFourHoursTimeString : formattedDueDate }}
            </div>
        </div>
        <div v-dark class="assignment-card__number-of-questions">
            {{ assignment.questionSerials.length }} Question{{ assignment.questionSerials.length > 1 ? 's' : '' }}
        </div>
    </div>
</template>

<style lang="scss" scoped>
.assignment-card {
    position: relative;
    background-color: white;
    border-radius: 5px;
    border: 1px solid rgba($pewter, 0.85);
    box-shadow: 0 1px 6px 0 rgba($brand-black, 0.3);
    box-sizing: border-box;
    width: 412px;
    min-height: 68px;
    padding: 12px 15px;
    outline: none;
    margin-bottom: 10px;
    cursor: pointer;

    @include breakpoint(black-bear) {
        width: 100%;
    }

    &--dark {
        background-color: $brand-black;
        border: 1px solid $slate;
        box-shadow: 0 1px 6px 0 rgba($charcoal, 0.3);
    }

    &:hover,
    &:focus {
        &::before {
            content: '';
            position: absolute;
            border-radius: 6px;
            border: 2px solid $pewter;
            top: -2px;
            bottom: -2px;
            left: -2px;
            right: -2px;
        }
    }

    &--selected {
        &::before {
            content: '';
            position: absolute;
            border-radius: 6px;
            border: 2px solid $brand-blue !important;
            top: -1px;
            bottom: -1px;
            left: -1px;
            right: -1px;
        }

        &--dark::before {
            border-color: $banana-bread !important;
        }
    }

    &__title-info {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
    }

    &__name {
        color: $brand-black;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        margin-bottom: 4px;
        width: 245px;

        &--dark {
            color: $barely-background;
        }

        @include breakpoint(black-bear) {
            width: 185px;
        }
    }

    &__due-date {
        display: flex;
        align-items: flex-start;
        color: $ash;
        font-size: 14px;
        font-weight: 500;
        line-height: 19px;

        &--dark {
            color: $fog;
        }

        &--today {
            color: $pepper;

            &--dark {
                color: $rosa;
            }
        }
    }

    &__warning-icon {
        margin-right: 4px;
    }

    &__number-of-questions {
        color: $ash;
        font-size: 14px;
        font-weight: 500;
        line-height: 19px;

        &--dark {
            color: $fog;
        }
    }
}
</style>
